import axios from 'axios';
import { TOKEN_EXPIRED } from '../hooks/useAPIHelper';
import { normaliseRow } from './pmCallPagesShape';

/**
 * A customer's whole PM-call list from paged files (PORTAL-72), or null to mean "use the old path".
 *
 * WHY IT RETURNS NULL RATHER THAN THROWING. This is an optimisation, and the call it replaces
 * (xServiceGetPMCalls) is the one that has always worked. Anything this path cannot do - a build
 * still running, a page that will not load, a page whose row count disagrees with the manifest, the
 * site not being set up for it - is answered by returning null, and the caller falls back to the old
 * call. The one exception is an expired Pronto token, which the old path would also report, so it is
 * thrown in the same words and the caller's existing handling applies.
 *
 * THE BROWSER FETCHES THE PAGES ITSELF, with no credential: each URL is signed, read-only and lasts
 * about 30 minutes. fetch() is used instead of axios on purpose, so nothing in the portal's axios
 * defaults (an Authorization header, say) can reach the storage account, which would refuse it.
 *
 * NULL BECOMES ''. The old path's XML parser turned an empty element into '', and
 * computePlannedStatus tests StatusCode == '' - so a null from the page files would leave the status
 * unset and the job would not read as Open. Normalised here rather than in every consumer.
 */

const PAGES_URL = '/.netlify/functions/xServiceGetPMCallPages';

/** How long to wait for a build that was started by this very request before giving up on it. */
const MAX_BUILD_WAIT_MS = 25000;

interface ManifestFile {
  n: number;
  url: string;
  rows: number;
}

interface Manifest {
  pages: number;
  rows: number;

  // When the data was last copied from the Extract (ISO, UTC); null or absent where unknown.
  dataAsOfUtc?: string | null;
  files: ManifestFile[];
}

// The as-of time of the list this browser last read from pages, by customer, so Home can show it and later
// tell cheaply whether a fresh read is needed.
const lastAsOf: Record<string, string | null> = {};

// Customers whose list this browser last read was only the FIRST page, because Home needs the newest few rows and
// the counts come from the database. Such a list must never be counted in the browser.
const lastPartial: Record<string, boolean> = {};

export const plannedListIsPartial = (customerId: string): boolean => lastPartial[customerId] === true;

export const plannedAsOfFor = (customerId: string): string | null => lastAsOf[customerId] ?? null;

/**
 * The as-of time of the customer's CURRENT set, from the manifest alone - a few hundred bytes, no pages. Used to
 * decide whether a list already held needs reading again. Returns the ISO time, null where the set carries none,
 * or undefined where it could not be asked (a build running, an error): the caller keeps what it has.
 */
export const fetchPlannedAsOf = async (token: string, customerId: string): Promise<string | null | undefined> => {
  try {
    const reply = await axios.post(
      PAGES_URL,
      `<Filters><CustomerCode><Like>${customerId}</Like></CustomerCode></Filters>`,
      {
        headers: { 'Content-Type': 'application/xml', 'X-Pronto-Token': token },
        validateStatus: (status) => status === 200 || status === 202,
      },
    );

    if (reply.status !== 200) {
      return undefined;
    }

    return (reply.data as Manifest).dataAsOfUtc ?? null;
  } catch (_) {
    return undefined;
  }
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** On for a browser that opted in, or for a site built with the flag. Off by default. */
export function pmCallPagesEnabled(): boolean {
  try {
    if (process.env.PM_CALL_PAGES === '1') {
      return true;
    }

    return typeof window !== 'undefined' && window.localStorage.getItem('pmCallPages') === '1';
  } catch (_) {
    return false;
  }
}

async function readPage(file: ManifestFile): Promise<any[]> {
  const reply = await fetch(file.url, { method: 'GET', credentials: 'omit' });

  if (!reply.ok) {
    throw new Error(`page ${file.n} answered HTTP ${reply.status}`);
  }

  const rows = await reply.json();

  if (!Array.isArray(rows)) {
    throw new Error(`page ${file.n} is not a list`);
  }

  return rows;
}

const GetPMCallPages = async (token: string, customerId: string, firstPageOnly: boolean = false): Promise<any[] | null> => {
  const started = Date.now();
  const body = `<Filters><CustomerCode><Like>${customerId}</Like></CustomerCode></Filters>`;

  // Until a read from pages succeeds there is no as-of time; a fall back to the live call must not keep an old one.
  lastAsOf[customerId] = null;
  lastPartial[customerId] = false;

  try {
    let manifest: Manifest | null = null;

    while (manifest === null) {
      const reply = await axios.post(PAGES_URL, body, {
        headers: { 'Content-Type': 'application/xml', 'X-Pronto-Token': token },
        validateStatus: (status) => status === 200 || status === 202,
      });

      if (reply.status === 200) {
        manifest = reply.data as Manifest;
        break;
      }

      // 202: a build is running. Wait if it is likely to finish soon, otherwise let the old path
      // serve this load - the build carries on and the next load finds it.
      const retryMs = Math.max(1, Number(reply.data?.retryAfterSeconds ?? 5)) * 1000;

      if (Date.now() - started + retryMs > MAX_BUILD_WAIT_MS) {
        console.log('PM call pages still building - using the standard path for this load');
        return null;
      }

      await wait(retryMs);
    }

    // Pages are cut in display order, so the first one holds the newest rows. Reading only it is what lets Home
    // show its recent list without waiting for the rest.
    const wanted = firstPageOnly ? manifest.files.slice(0, 1) : manifest.files;
    const pages = await Promise.all(wanted.map((file) => readPage(file)));

    lastPartial[customerId] = wanted.length < manifest.files.length;

    lastAsOf[customerId] = manifest.dataAsOfUtc ?? null;

    // The manifest states how many rows each page holds. A page that disagrees was damaged or cut
    // short, and passing it on would show a customer fewer jobs with nothing to say so.
    pages.forEach((rows, index) => {
      if (rows.length !== manifest!.files[index].rows) {
        throw new Error(`page ${manifest!.files[index].n} holds ${rows.length} rows, the manifest says ${manifest!.files[index].rows}`);
      }
    });

    const jobs = pages.flat().map(normaliseRow);

    console.log(`PM call pages: ${jobs.length} rows from ${pages.length} of ${manifest.files.length} page(s) in ${Date.now() - started} ms`);

    return jobs;
  } catch (err: any) {
    if (err?.response?.status === 401 && err.response.data === 'TokenExpired') {
      throw TOKEN_EXPIRED;
    }

    console.log('PM call pages unavailable - using the standard path:', err?.message ?? err);
    return null;
  }
};

export default GetPMCallPages;
